import {
  COMPONENT_OR_REFERENCE_DESCRIPTION_FIELD_LABEL,
  ComponentLabelSource,
} from '@ardoq/data-model';
import { isObject, omit } from 'lodash';
import {
  matchSelectedReferenceType,
  type SelectedReferenceTypeInfo,
  type TraversedReferenceTypeInfo,
} from '@ardoq/settings-bar';
import type { APIFieldAttributes } from '@ardoq/api-types';
import type { FieldWithPermissions } from 'streams/fields/getFieldsForViewpoint';
import {
  COMPONENT_MATRIX_2_LAYOUT_SETTING_KEYS,
  type ComponentMatrix2AiViewProperties,
  type ComponentMatrix2AxisSources,
  type ComponentMatrix2SettingsBounds,
  type ComponentMatrix2SettingsControlsDependencies,
  type ComponentMatrix2StoredSettableSettings,
  type ComponentMatrix2ViewSettings,
  type ComponentMatrix2ViewSettingsState,
} from './types';

const DEFAULT_HEADER_FIELD_NAMES: string[] = [
  ComponentLabelSource.TYPE,
  COMPONENT_OR_REFERENCE_DESCRIPTION_FIELD_LABEL,
];

const selectableFieldNames = (fields: FieldWithPermissions[]): string[] =>
  fields.filter(field => !field.isConfidential).map(field => field.name);

const headerFieldNames = (fields: FieldWithPermissions[]): string[] => [
  ...DEFAULT_HEADER_FIELD_NAMES,
  ...selectableFieldNames(fields),
];

const toAxisReferences = ({
  traversedIncomingReferenceTypes,
  traversedOutgoingReferenceTypes,
}: ComponentMatrix2ViewSettingsState): TraversedReferenceTypeInfo[] => [
  ...traversedIncomingReferenceTypes,
  ...traversedOutgoingReferenceTypes,
];

export const toComponentMatrix2SettingsBounds = (
  viewSettingsState: ComponentMatrix2ViewSettingsState
): ComponentMatrix2SettingsBounds => {
  const {
    availableComponentTypes,
    listFields,
    additionalHeaderFieldsX,
    additionalHeaderFieldsY,
  } = viewSettingsState;
  const axisReferences = toAxisReferences(viewSettingsState);

  return {
    availableComponentTypes,
    axisFieldNames: selectableFieldNames(listFields),
    axisHeaderFieldNamesX: headerFieldNames(additionalHeaderFieldsX),
    axisHeaderFieldNamesY: headerFieldNames(additionalHeaderFieldsY),
    axisReferences,
    canSetAxes: listFields.length > 0 || axisReferences.length > 0,
  };
};

const resolvedFieldNames = (
  selectedFieldNames: string[],
  listFields: APIFieldAttributes[]
): string[] =>
  selectedFieldNames.filter(fieldName =>
    listFields.some(field => field.name === fieldName)
  );

const matchedAxisReference = (
  referenceType: SelectedReferenceTypeInfo | null,
  axisReferences: TraversedReferenceTypeInfo[]
): TraversedReferenceTypeInfo | null => {
  if (referenceType === null) return null;
  return (
    axisReferences.find(axisReference =>
      matchSelectedReferenceType(referenceType, axisReference)
    ) ?? null
  );
};

const resolvedHeaderFieldNames = (
  additionalHeaderFieldNames: string[],
  additionalHeaderFields: FieldWithPermissions[]
): string[] => {
  const offered = headerFieldNames(additionalHeaderFields);
  return additionalHeaderFieldNames.filter(fieldName =>
    offered.includes(fieldName)
  );
};

type ComponentMatrix2AxisSourcesArgs = {
  viewSettings: ComponentMatrix2ViewSettings;
  listFields: APIFieldAttributes[];
  axisReferences: TraversedReferenceTypeInfo[];
  additionalHeaderFieldsX: FieldWithPermissions[];
  additionalHeaderFieldsY: FieldWithPermissions[];
};

export const toComponentMatrix2AxisSources = ({
  viewSettings,
  listFields,
  axisReferences,
  additionalHeaderFieldsX,
  additionalHeaderFieldsY,
}: ComponentMatrix2AxisSourcesArgs): ComponentMatrix2AxisSources => ({
  fieldNamesX: resolvedFieldNames(viewSettings.selectedFieldNamesX, listFields),
  fieldNamesY: resolvedFieldNames(viewSettings.selectedFieldNamesY, listFields),
  referenceX: matchedAxisReference(viewSettings.referenceTypeX, axisReferences),
  referenceY: matchedAxisReference(viewSettings.referenceTypeY, axisReferences),
  headerFieldNamesX: resolvedHeaderFieldNames(
    viewSettings.additionalHeaderFieldNamesX,
    additionalHeaderFieldsX
  ),
  headerFieldNamesY: resolvedHeaderFieldNames(
    viewSettings.additionalHeaderFieldNamesY,
    additionalHeaderFieldsY
  ),
});

type ComponentMatrix2StoredSettableSettingsArgs = {
  viewSettings: ComponentMatrix2ViewSettings;
  selectedComponentTypes: string[];
  axisSources: ComponentMatrix2AxisSources;
};

export const toComponentMatrix2StoredSettableSettings = ({
  viewSettings,
  selectedComponentTypes,
  axisSources,
}: ComponentMatrix2StoredSettableSettingsArgs): ComponentMatrix2StoredSettableSettings => ({
  ...omit(viewSettings, COMPONENT_MATRIX_2_LAYOUT_SETTING_KEYS),
  selectedComponentTypes,
  selectedFieldNamesX: axisSources.fieldNamesX,
  selectedFieldNamesY: axisSources.fieldNamesY,
  referenceTypeX:
    axisSources.referenceX === null ? null : viewSettings.referenceTypeX,
  referenceTypeY:
    axisSources.referenceY === null ? null : viewSettings.referenceTypeY,
  additionalHeaderFieldNamesX: axisSources.headerFieldNamesX,
  additionalHeaderFieldNamesY: axisSources.headerFieldNamesY,
});

/**
 * The bounds the AI view settings context publishes. The JSDoc on
 * `SettableComponentMatrix2ViewSettings` points the model at these by name, so
 * a key removed here leaves those descriptions naming something absent.
 */
export const COMPONENT_MATRIX_2_AI_VIEW_STATE_KEYS = [
  'availableComponentTypes',
  'axisFieldNames',
  'axisReferences',
  'axisHeaderFieldNamesX',
  'axisHeaderFieldNamesY',
  'canSetAxes',
] as const satisfies readonly (keyof ComponentMatrix2SettingsBounds)[];

export const AI_COMPONENT_MATRIX_2_PROPERTY_KEYS = [
  'settingsBounds',
  'settableViewSettings',
] as const satisfies readonly (keyof ComponentMatrix2AiViewProperties)[];

const hasComponentMatrix2SettingsBounds = ({
  settingsBounds,
  settableViewSettings,
}: Record<string, unknown>): boolean =>
  isObject(settingsBounds) &&
  COMPONENT_MATRIX_2_AI_VIEW_STATE_KEYS.every(key => key in settingsBounds) &&
  isObject(settableViewSettings);

export const isComponentMatrix2AiViewProperties = (
  viewProperties: Record<string, unknown>
): viewProperties is ComponentMatrix2AiViewProperties =>
  hasComponentMatrix2SettingsBounds(viewProperties);

export const isComponentMatrix2SettingsControlsDependencies = (
  viewProperties: Record<string, unknown>
): viewProperties is ComponentMatrix2SettingsControlsDependencies =>
  hasComponentMatrix2SettingsBounds(viewProperties) &&
  isObject(viewProperties.viewSettings) &&
  isObject(viewProperties.viewSettingsState) &&
  isObject(viewProperties.userLocale);
